import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import WelcomePackEditor from "../components/WelcomePackEditor";
import ContactPageEditor from "../components/ContactPageEditor";
import StoriesAdmin from "../components/StoriesAdmin";
import MaintenanceTab from "../components/MaintenanceTab";
import StudentLocationsMap from "../components/StudentLocationsMap";
import StateWaitlistTab from "../components/StateWaitlistTab";
import ReportsTab from "../components/ReportsTab";
import ESignaturesTab from "../components/ESignaturesTab";
import AdminSidebar from "../components/AdminSidebar";
import AdminOverview from "../components/AdminOverview";
import ParticipantNotesPanel from "../components/ParticipantNotesPanel";
import ParticipantCasePanel from "../components/ParticipantCasePanel";
import ApprenticeshipTab from "../components/ApprenticeshipTab";
import ArchitectureTab from "../components/ArchitectureTab";
import InstructorLibraryPanel from "../components/InstructorLibraryPanel";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { 
  Settings, Users, FileText, BookOpen, GraduationCap, CheckCircle,
  XCircle, Clock, Plus, Search, Eye, ChevronRight, ArrowRight,
  Layers, Award, Pencil, Trash2, X, Image, CalendarDays, ChevronUp, ChevronDown, Copy, Archive,
  Upload, FileSpreadsheet, AlertCircle, UserPlus, ListOrdered, CreditCard, Shield, Download, Trash2 as Trash2Icon, Rocket, Cloud, CloudOff, Heart, ShieldCheck, StickyNote, Ban, Briefcase
} from "lucide-react";


// ============================================
// DROPBOX CONNECT + EXPORT
// ============================================
const DropboxConnectCard = () => {
  const [status, setStatus] = useState({ loading: true, connected: false, configured: false });
  const [connecting, setConnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/dropbox/status`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setStatus({ loading: false, ...d });
      } else {
        setStatus(s => ({ ...s, loading: false }));
      }
    } catch {
      setStatus(s => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // If we just came back from the OAuth callback, show a toast and refresh
    const params = new URLSearchParams(window.location.search);
    const dropboxFlag = params.get("dropbox");
    if (dropboxFlag === "connected") {
      toast.success("Dropbox connected successfully!");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (dropboxFlag === "error") {
      toast.error(`Dropbox connection failed: ${params.get("detail") || "unknown"}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [fetchStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`${API}/admin/dropbox/authorize-url`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const d = await res.json();
        window.location.href = d.authorize_url;
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to start Dropbox connection");
        setConnecting(false);
      }
    } catch {
      toast.error("Network error");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect your Dropbox account?")) return;
    const res = await fetch(`${API}/admin/dropbox/disconnect`, {
      method: "POST",
      credentials: "include",
    });
    if (res.ok) {
      toast.success("Dropbox disconnected");
      fetchStatus();
    }
  };

  if (status.loading) return null;
  if (!status.configured) {
    return (
      <Card className="card-surface border-amber-200 bg-amber-50/40" data-testid="dropbox-not-configured">
        <CardContent className="pt-4 pb-4">
          <p className="text-sm text-amber-800 flex items-center gap-2">
            <CloudOff className="w-4 h-4" /> Dropbox is not configured on the server. Set <code>DROPBOX_APP_KEY</code> and <code>DROPBOX_APP_SECRET</code> in backend env.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-surface" data-testid="dropbox-connect-card">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {status.connected ? (
              <Cloud className="w-5 h-5 text-[#0061FF]" />
            ) : (
              <CloudOff className="w-5 h-5 text-slate-400" />
            )}
            <div>
              <p className="text-sm font-semibold text-slate-900">
                Dropbox {status.connected ? "Connected" : "Not Connected"}
              </p>
              <p className="text-xs text-slate-500">
                {status.connected
                  ? `${status.account_email || "your Dropbox"} · Connected ${status.connected_at ? new Date(status.connected_at).toLocaleDateString() : ""}`
                  : "Connect to export student records to your Dropbox folder."}
              </p>
            </div>
          </div>
          {status.connected ? (
            <Button variant="outline" onClick={handleDisconnect} data-testid="dropbox-disconnect-btn">
              Disconnect
            </Button>
          ) : (
            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="bg-[#0061FF] hover:bg-[#004BCC] text-white"
              data-testid="dropbox-connect-btn"
            >
              <Cloud className="w-4 h-4 mr-2" /> {connecting ? "Redirecting…" : "Connect Dropbox"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const DropboxExportButton = ({ userId, userEmail }) => {
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleExport = async () => {
    if (!userId) return;
    setExporting(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/admin/dropbox/export/${userId}`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("Exported to Dropbox!");
        setResult(data);
      } else {
        toast.error(data.detail || "Export failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-[#0061FF]/5 border border-[#0061FF]/20 p-3 rounded-lg" data-testid="dropbox-export-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Cloud className="w-4 h-4 text-[#0061FF]" />
          <p className="text-sm text-slate-700">
            Export this student’s full record (PDF + JSON + documents) to Dropbox
          </p>
        </div>
        <Button
          size="sm"
          onClick={handleExport}
          disabled={exporting || !userId}
          className="bg-[#0061FF] hover:bg-[#004BCC] text-white"
          data-testid="dropbox-export-btn"
        >
          {exporting ? (
            <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Exporting…</>
          ) : (
            <><Cloud className="w-4 h-4 mr-2" /> Export to Dropbox</>
          )}
        </Button>
      </div>
      {result && (
        <p className="text-xs text-slate-500 mt-2 break-all" data-testid="dropbox-export-result">
          ✓ Uploaded to <code>{result.dropbox_path}</code> ({result.documents_included} document{result.documents_included === 1 ? "" : "s"})
        </p>
      )}
    </div>
  );
};


// ============================================
// KANBAN PIPELINE TAB
// ============================================
const PIPELINE_COLUMNS = [
  { key: "draft", label: "Draft", color: "#64748B" },
  { key: "intake_complete", label: "Intake Done", color: "#3B82F6" },
  { key: "eligibility_complete", label: "Eligibility Done", color: "#8B5CF6" },
  { key: "application_complete", label: "Application Done", color: "#F59E0B" },
  { key: "submitted", label: "Submitted", color: "#FFD507" },
  { key: "under_review", label: "Under Review", color: "#F59E0B" },
  { key: "approved", label: "Approved", color: "#10B981" },
  { key: "rejected", label: "Rejected", color: "#EF4444" },
  { key: "waitlisted", label: "Waitlisted", color: "#7C3AED" },
  { key: "transferred", label: "Transferred", color: "#94A3B8" },
];

const IssueCertificationButton = ({ userId, userName, programName, programId, cohortId }) => {
  const [open, setOpen] = useState(false);
  const [courseName, setCourseName] = useState(programName || "");
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState(null);

  useEffect(() => {
    setCourseName(programName || "");
  }, [programName]);

  const handleIssue = async () => {
    if (!courseName.trim()) {
      toast.error("Please enter the program name");
      return;
    }
    if (!window.confirm(`Issue Aspiritech Verified Certification to ${userName} for "${courseName}"?\n\nThis will mark them as Alumni and is logged for audit.`)) return;
    setIssuing(true);
    try {
      const res = await fetch(`${API}/instructor/certifications/issue`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_id: userId,
          course_name: courseName.trim(),
          program_id: programId || null,
          cohort_id: cohortId || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("Certification issued! 🎉");
        setIssued(data);
      } else {
        toast.error(data.detail || "Failed to issue");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setIssuing(false);
    }
  };

  return (
    <div className="bg-[#FFD507]/10 border border-[#FFD507]/40 p-3 rounded-lg" data-testid="issue-cert-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#1E3A8A]" />
          <p className="text-sm text-slate-700 font-medium">
            Issue Program Certificate
            {programId && <span className="ml-1 text-xs text-slate-500">· {programName}</span>}
          </p>
        </div>
        {!open && !issued && (
          <Button size="sm" onClick={() => setOpen(true)} className="bg-[#FFD507] hover:bg-[#FCC100] text-slate-900 font-semibold" data-testid="open-issue-cert-btn">
            Issue Certificate
          </Button>
        )}
      </div>
      {open && !issued && (
        <div className="mt-3 space-y-2">
          <div>
            <Label className="text-xs text-slate-600">Program name on the certificate</Label>
            <Input
              value={courseName}
              onChange={(e) => setCourseName(e.target.value)}
              className="input-base mt-1"
              placeholder="e.g., Quality Assurance Program"
              data-testid="cert-course-input"
            />
            {programId && (
              <p className="text-[11px] text-slate-500 mt-1">
                Linked to program <code>{programId}</code>{cohortId ? ` · cohort ${cohortId}` : ""}. The PDF will include cohort dates.
              </p>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleIssue} disabled={issuing} className="bg-[#FFD507] hover:bg-[#FCC100] text-slate-900 font-semibold" data-testid="confirm-issue-cert-btn">
              {issuing ? "Issuing…" : "🏅 Issue & Mark Alumni"}
            </Button>
          </div>
        </div>
      )}
      {issued && (
        <div className="mt-3 p-3 bg-white border border-emerald-300 rounded-lg" data-testid="cert-issued-confirmation">
          <p className="text-sm font-semibold text-emerald-700 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Certification issued!
          </p>
          <p className="text-xs text-slate-600 mt-1">
            Verification ID: <span className="font-mono">{issued.verification_id}</span>
          </p>
          <p className="text-xs text-slate-500 mt-1 break-all">
            Verify URL: <a href={`/verify/${issued.verification_id}`} target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] hover:underline">/verify/{issued.verification_id}</a>
          </p>
        </div>
      )}
    </div>
  );
};



const HEALTH_DOT = {
  red: { color: "#EF4444", label: "Needs attention" },
  yellow: { color: "#F59E0B", label: "Follow up soon" },
  green: { color: "#10B981", label: "On track" },
  closed: { color: "#94A3B8", label: "Closed" },
};

const HealthDot = ({ health, testid }) => {
  if (!health) return null;
  const h = HEALTH_DOT[health.color] || HEALTH_DOT.closed;
  const title = `${h.label}${health.reasons?.length ? " — " + health.reasons.join("; ") : ""}`;
  return (
    <span className="inline-flex items-center shrink-0" title={title} data-testid={testid} data-health={health.color}>
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: h.color }} />
    </span>
  );
};

const PipelineCard = ({ enrollment, onView, onStatusChange }) => (
  <div
    className="bg-white border border-slate-200 rounded-lg p-3 mb-2 hover:border-brand-gold/40 transition-colors cursor-pointer group"
    data-testid={`pipeline-card-${enrollment.enrollment_id}`}
    onClick={() => onView(enrollment)}
  >
    <div className="flex items-center gap-2 mb-2">
      <HealthDot health={enrollment.health} testid={`pipeline-health-${enrollment.enrollment_id}`} />
      {enrollment.user_picture ? (
        <img src={enrollment.user_picture} alt="" className="w-7 h-7 rounded-full" />
      ) : (
        <div className="w-7 h-7 rounded-full bg-[#1E3A8A] flex items-center justify-center text-xs text-slate-900 font-bold">
          {(enrollment.applicant_name || "?")[0]}
        </div>
      )}
      <span className="text-sm text-slate-900 font-medium truncate">{enrollment.applicant_name || enrollment.user_name}</span>
    </div>
    <p className="text-xs text-slate-500 truncate">{enrollment.user_email}</p>
    <div className="flex items-center gap-2 mt-1">
      <p className="text-xs text-slate-500">
        {new Date(enrollment.created_at).toLocaleDateString()}
      </p>
      {enrollment.payment_status && enrollment.payment_status !== "not_required" && (
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
          enrollment.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" :
          "bg-amber-100 text-amber-700"
        }`} data-testid={`pipeline-payment-${enrollment.enrollment_id}`}>
          {enrollment.payment_status === "paid" ? "$99 Paid" : "$99 Pending"}
        </span>
      )}
    </div>
    {enrollment.status === "submitted" && (
      <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button size="sm" className="h-6 px-2 text-xs bg-[#10B981] hover:bg-[#059669] text-slate-900"
          onClick={(e) => { e.stopPropagation(); onStatusChange(enrollment.enrollment_id, "approved"); }}>
          Approve
        </Button>
        <Button size="sm" className="h-6 px-2 text-xs bg-[#EF4444] hover:bg-[#DC2626] text-slate-900"
          onClick={(e) => { e.stopPropagation(); onStatusChange(enrollment.enrollment_id, "rejected"); }}>
          Reject
        </Button>
      </div>
    )}
  </div>
);

const PipelineTab = ({ enrollments, onStatusChange, onViewDetail }) => {
  const grouped = {};
  PIPELINE_COLUMNS.forEach(col => { grouped[col.key] = []; });
  enrollments.forEach(e => {
    if (grouped[e.status]) grouped[e.status].push(e);
  });

  return (
    <div className="overflow-x-auto pb-4" data-testid="pipeline-board">
      <div className="flex items-center gap-4 mb-3 text-xs text-slate-500" data-testid="health-legend">
        <span className="font-medium text-slate-600">Health:</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#10B981" }} /> On track</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#F59E0B" }} /> No contact 5+ business days</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#EF4444" }} /> No contact 10+ days or missing docs</span>
      </div>
      <div className="flex gap-4 min-w-[1100px]">
        {PIPELINE_COLUMNS.map(col => (
          <div key={col.key} className="flex-1 min-w-[180px]">
            <div className="flex items-center gap-2 mb-3 px-1">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
              <span className="text-sm font-medium text-slate-600">{col.label}</span>
              <span className="text-xs text-slate-500 ml-auto bg-surface-muted px-2 py-0.5 rounded-full">
                {grouped[col.key].length}
              </span>
            </div>
            <div className="bg-slate-500 border border-slate-100 rounded-lg p-2 min-h-[300px]">
              {grouped[col.key].map(enrollment => (
                <PipelineCard
                  key={enrollment.enrollment_id}
                  enrollment={enrollment}
                  onView={onViewDetail}
                  onStatusChange={onStatusChange}
                />
              ))}
              {grouped[col.key].length === 0 && (
                <p className="text-xs text-slate-600 text-center py-8">No applicants</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// ENROLLMENT DETAIL DIALOG
// ============================================
const DataSection = ({ title, data }) => {
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-brand-navy uppercase tracking-wider">{title}</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
        {Object.entries(data).map(([key, value]) => {
          if (value === null || value === undefined || value === "" || key.startsWith("_")) return null;
          const label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
          let displayValue = value;
          if (typeof value === "boolean") displayValue = value ? "Yes" : "No";
          if (Array.isArray(value)) displayValue = value.join(", ") || "None";
          if (typeof value === "object" && !Array.isArray(value)) displayValue = JSON.stringify(value);
          return (
            <div key={key} className="py-1">
              <span className="text-xs text-slate-500">{label}</span>
              <p className="text-sm text-slate-200 break-words">{String(displayValue)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EnrollmentDetailDialog = ({ enrollment, open, onClose, onStatusChange }) => {
  const [activeSection, setActiveSection] = useState("profile");
  if (!enrollment) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-3">
            <FileText className="w-5 h-5 text-brand-navy" />
            {enrollment.applicant_name || enrollment.user_name}
          </DialogTitle>
          <DialogDescription className="text-slate-500 sr-only">Full enrollment details and status</DialogDescription>
        </DialogHeader>

        {/* Inner section toggle: Profile vs Case Notes */}
        <div className="flex items-center gap-2 border-b border-slate-200 -mt-1 pb-1">
          <button
            onClick={() => setActiveSection("profile")}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition
              ${activeSection === "profile" ? "border-brand-navy text-brand-navy" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="enrollment-detail-tab-profile"
          >
            Enrollment
          </button>
          <button
            onClick={() => setActiveSection("notes")}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition inline-flex items-center gap-1.5
              ${activeSection === "notes" ? "border-brand-navy text-brand-navy" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="enrollment-detail-tab-notes"
          >
            <StickyNote className="w-3.5 h-3.5" /> Case Notes
          </button>
          <button
            onClick={() => setActiveSection("outcomes")}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition inline-flex items-center gap-1.5
              ${activeSection === "outcomes" ? "border-brand-navy text-brand-navy" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="enrollment-detail-tab-outcomes"
          >
            <Briefcase className="w-3.5 h-3.5" /> Outcomes
          </button>
        </div>

        <ScrollArea className="max-h-[65vh] pr-3">
          {activeSection === "notes" ? (
            <div className="py-3">
              <ParticipantNotesPanel
                participantId={enrollment.user_id}
                participantName={enrollment.applicant_name || enrollment.user_name}
              />
            </div>
          ) : activeSection === "outcomes" ? (
            <div className="py-3">
              <ParticipantCasePanel
                participantId={enrollment.user_id}
                participantName={enrollment.applicant_name || enrollment.user_name}
              />
            </div>
          ) : (
          <div className="space-y-5 py-2">
            <div className="flex items-center justify-between bg-surface-muted p-3 rounded-lg">
              <div>
                <p className="text-xs text-slate-500">Status</p>
                <Badge className={`mt-1 ${getStatusStyle(enrollment.status)}`}>
                  {enrollment.status?.replace(/_/g, " ")}
                </Badge>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Step</p>
                <p className="text-sm text-slate-900">{enrollment.current_step} / 3</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Created</p>
                <p className="text-sm text-slate-600">{new Date(enrollment.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            {/* Payment Status Section */}
            {enrollment.payment_status && enrollment.payment_status !== "not_required" && (
              <div className="bg-surface-muted p-3 rounded-lg space-y-2" data-testid="enrollment-payment-info">
                <h4 className="text-xs font-semibold text-brand-navy uppercase tracking-wider flex items-center gap-2">
                  <CreditCard className="w-3.5 h-3.5" /> Payment Information
                </h4>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">Status</p>
                    <Badge className={`mt-1 ${
                      enrollment.payment_status === "paid" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                      enrollment.payment_status === "pending" ? "bg-amber-100 text-amber-700 border-amber-200" :
                      "bg-slate-100 text-slate-600 border-slate-200"
                    }`} data-testid="payment-status-badge">
                      {enrollment.payment_status === "paid" ? "Paid" : enrollment.payment_status === "pending" ? "Pending" : enrollment.payment_status}
                    </Badge>
                  </div>
                  {enrollment.payment_amount && (
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Amount</p>
                      <p className="text-sm font-semibold text-slate-900" data-testid="payment-amount">${enrollment.payment_amount}</p>
                    </div>
                  )}
                  {enrollment.stripe_session_id && (
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Session</p>
                      <p className="text-xs text-slate-500 font-mono" data-testid="stripe-session-id" title={enrollment.stripe_session_id}>
                        {enrollment.stripe_session_id.length > 20 ? `${enrollment.stripe_session_id.slice(0, 20)}...` : enrollment.stripe_session_id}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            <DataSection title="Intake Information" data={enrollment.intake} />
            <DataSection title="Eligibility Information" data={enrollment.eligibility} />
            <DataSection title="Program Application" data={enrollment.program_application} />
            {enrollment.admin_notes && (
              <div>
                <h4 className="text-sm font-semibold text-brand-navy uppercase tracking-wider">Admin Notes</h4>
                <p className="text-sm text-slate-600 mt-1">{enrollment.admin_notes}</p>
              </div>
            )}

            {/* Dropbox Export */}
            <DropboxExportButton userId={enrollment.user_id} userEmail={enrollment.user_email} />

            {/* Issue Certification */}
            <IssueCertificationButton
              userId={enrollment.user_id}
              userName={enrollment.user_name}
              programName={enrollment.program_application?.program_name}
              programId={enrollment.program_application?.program_id || enrollment.program_selection?.program_id || enrollment.cohort_selection?.program_id}
              cohortId={enrollment.program_selection?.cohort_id || enrollment.cohort_selection?.cohort_id}
            />

            {enrollment.status === "submitted" && (
              <div className="flex gap-3 pt-2">
                <Button className="flex-1 bg-[#10B981] hover:bg-[#059669] text-slate-900"
                  onClick={() => { onStatusChange(enrollment.enrollment_id, "approved"); onClose(false); }}>
                  <CheckCircle className="w-4 h-4 mr-2" /> Approve
                </Button>
                <Button className="flex-1 bg-[#EF4444] hover:bg-[#DC2626] text-slate-900"
                  onClick={() => { onStatusChange(enrollment.enrollment_id, "rejected"); onClose(false); }}>
                  <XCircle className="w-4 h-4 mr-2" /> Reject
                </Button>
              </div>
            )}
          </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// PROGRAMS CMS TAB
// ============================================
const ProgramFormDialog = ({ program, open, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: "", title: "", description: "", photo_url: "", certifications: [], duration_weeks: "", sort_order: 0
  });
  const [newCert, setNewCert] = useState("");

  useEffect(() => {
    if (program) {
      setForm({
        name: program.name || "",
        title: program.title || "",
        description: program.description || "",
        photo_url: program.photo_url || "",
        certifications: program.certifications || [],
        duration_weeks: program.duration_weeks || "",
        sort_order: program.sort_order || 0
      });
    } else {
      setForm({ name: "", title: "", description: "", photo_url: "", certifications: [], duration_weeks: "", sort_order: 0 });
    }
  }, [program, open]);

  const addCert = () => {
    if (newCert.trim()) {
      setForm(prev => ({ ...prev, certifications: [...prev.certifications, newCert.trim()] }));
      setNewCert("");
    }
  };

  const removeCert = (i) => {
    setForm(prev => ({ ...prev, certifications: prev.certifications.filter((_, idx) => idx !== i) }));
  };

  const handleSave = () => {
    if (!form.name || !form.title || !form.description) {
      toast.error("Name, title, and description are required");
      return;
    }
    const payload = {
      ...form,
      duration_weeks: form.duration_weeks ? parseInt(form.duration_weeks) : null,
      photo_url: form.photo_url || null
    };
    onSave(payload, program?.program_id);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-slate-900">{program ? "Edit Program" : "Create Program"}</DialogTitle>
          <DialogDescription className="text-slate-500 sr-only">Manage program details</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label className="text-slate-600">Program Name *</Label>
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="input-base" placeholder="e.g., QA Testing" data-testid="program-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-600">Title *</Label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              className="input-base" placeholder="e.g., Quality Assurance Testing Program" data-testid="program-title-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-600">Description *</Label>
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              className="input-base min-h-[100px]" data-testid="program-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-600">Duration (weeks)</Label>
              <Input type="number" value={form.duration_weeks} onChange={e => setForm(p => ({ ...p, duration_weeks: e.target.value }))}
                className="input-base" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600">Sort Order</Label>
              <Input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
                className="input-base" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-600">Photo URL</Label>
            <Input value={form.photo_url} onChange={e => setForm(p => ({ ...p, photo_url: e.target.value }))}
              className="input-base" placeholder="https://..." />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-600">Certifications</Label>
            <div className="flex gap-2">
              <Input value={newCert} onChange={e => setNewCert(e.target.value)} className="input-base"
                placeholder="Add certification..." onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCert(); } }} />
              <Button type="button" className="btn-secondary" onClick={addCert}><Plus className="w-4 h-4" /></Button>
            </div>
            {form.certifications.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.certifications.map((cert, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#1E3A8A]/30 text-[#3B82F6] rounded">
                    <Award className="w-3 h-3" />{cert}
                    <button onClick={() => removeCert(i)} className="ml-1 hover:text-red-400"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <Button onClick={handleSave} className="w-full btn-primary" data-testid="save-program-btn">
            {program ? "Update Program" : "Create Program"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CohortEditor = ({ cohort, programId, onSave, onDelete, onDuplicate }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    start_date: cohort.start_date || "",
    end_date: cohort.end_date || "",
    schedule: cohort.schedule || "",
    notes: cohort.notes || "",
    max_participants: cohort.max_participants || 15,
    instructors: (cohort.instructors || []).join(", "),
  });

  const handleSave = () => {
    onSave(programId, cohort.cohort_id, {
      start_date: form.start_date,
      end_date: form.end_date,
      schedule: form.schedule,
      notes: form.notes,
      max_participants: parseInt(form.max_participants) || 15,
      instructors: form.instructors.split(",").map(s => s.trim()).filter(Boolean),
    });
    setEditing(false);
  };

  const isFull = cohort.max_participants > 0 && cohort.enrolled_count >= cohort.max_participants;
  const spotsLeft = (cohort.max_participants || 0) - (cohort.enrolled_count || 0);
  const isArchived = cohort.is_archived || cohort.archived;

  return (
    <div className={`border rounded-lg p-3 bg-white ${isArchived ? "border-slate-200 opacity-60" : isFull ? "border-amber-300" : "border-slate-200"}`} data-testid={`cohort-editor-${cohort.cohort_id}`}>
      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Start Date</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                className="input-base mt-1" data-testid={`cohort-start-date-${cohort.cohort_id}`} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">End Date</Label>
              <Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                className="input-base mt-1" data-testid={`cohort-end-date-${cohort.cohort_id}`} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Schedule (days/times)</Label>
              <Input value={form.schedule} onChange={e => setForm(p => ({ ...p, schedule: e.target.value }))}
                className="input-base mt-1" placeholder="Mon–Fri, 9 a.m.–noon CT" data-testid={`cohort-schedule-${cohort.cohort_id}`} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Max Students</Label>
              <Input type="number" value={form.max_participants} onChange={e => setForm(p => ({ ...p, max_participants: e.target.value }))}
                className="input-base mt-1" min="1" data-testid={`cohort-max-${cohort.cohort_id}`} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Track / Credential note</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                className="input-base mt-1" placeholder="e.g. Beginners · CompTIA Tech+" data-testid={`cohort-notes-${cohort.cohort_id}`} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Instructors (comma-sep)</Label>
              <Input value={form.instructors} onChange={e => setForm(p => ({ ...p, instructors: e.target.value }))}
                className="input-base mt-1" placeholder="Name 1, Name 2" data-testid={`cohort-instructors-${cohort.cohort_id}`} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="btn-primary" onClick={handleSave} data-testid={`save-cohort-${cohort.cohort_id}`}>Save</Button>
            <Button size="sm" variant="outline" className="btn-outline" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <span className="text-sm font-medium text-slate-900">
                {cohort.start_date ? new Date(cohort.start_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "No date set"}
                {cohort.end_date ? ` – ${new Date(cohort.end_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
              </span>
              {cohort.schedule && <span className="text-xs text-slate-400 ml-2">({cohort.schedule})</span>}
              {cohort.notes && <span className="block text-xs text-brand-navy/70 mt-0.5">{cohort.notes}</span>}
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`text-xs border ${isFull ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                {cohort.enrolled_count || 0} / {cohort.max_participants || "∞"} enrolled
              </Badge>
              {isFull && <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">Full</Badge>}
              {!isFull && spotsLeft > 0 && spotsLeft <= 3 && (
                <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">{spotsLeft} spots left</Badge>
              )}
              {isArchived && (
                <Badge className="bg-slate-200 text-slate-600 border-slate-300 text-xs" data-testid={`archived-badge-${cohort.cohort_id}`}>
                  {cohort.archived ? "Archived" : "Completed"}
                </Badge>
              )}
            </div>
            {cohort.instructors?.length > 0 && (
              <span className="text-xs text-slate-400">{cohort.instructors.join(", ")}</span>
            )}
          </div>
          <div className="flex gap-1.5 flex-shrink-0">
            <Button size="sm" variant="outline" className="btn-outline h-7 text-xs" onClick={() => setEditing(true)} data-testid={`edit-cohort-${cohort.cohort_id}`}>
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="outline" className="btn-outline h-7 text-xs" onClick={() => onDuplicate(programId, cohort.cohort_id)} data-testid={`duplicate-cohort-${cohort.cohort_id}`}>
              <Copy className="w-3 h-3 mr-1" /> Duplicate
            </Button>
            <Button size="sm" variant="outline" className="btn-outline h-7 text-xs" onClick={() => onSave(programId, cohort.cohort_id, { archived: !cohort.archived })} data-testid={`archive-cohort-${cohort.cohort_id}`}>
              <Archive className="w-3 h-3 mr-1" /> {cohort.archived ? "Unarchive" : "Archive"}
            </Button>
            <Button size="sm" variant="outline" className="border-red-400/30 text-red-400 hover:bg-red-50 h-7 text-xs"
              onClick={() => onDelete(programId, cohort.cohort_id)} data-testid={`delete-cohort-${cohort.cohort_id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProgramsTab = ({ programs, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProgram, setEditProgram] = useState(null);
  const [expandedProgram, setExpandedProgram] = useState(null);

  const handleSave = async (payload, programId) => {
    try {
      const url = programId ? `${API}/programs/${programId}` : `${API}/programs`;
      const method = programId ? "PUT" : "POST";
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success(programId ? "Program updated" : "Program created");
        setDialogOpen(false);
        setEditProgram(null);
        onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch { toast.error("An error occurred"); }
  };

  const handleDelete = async (programId) => {
    if (!window.confirm("Deactivate this program?")) return;
    try {
      const res = await fetch(`${API}/programs/${programId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Program deactivated"); onRefresh(); }
    } catch { toast.error("Failed to deactivate"); }
  };

  const saveCohort = async (programId, cohortId, updates) => {
    try {
      const res = await fetch(`${API}/programs/${programId}/cohorts/${cohortId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(updates)
      });
      if (res.ok) { toast.success("Cohort updated"); onRefresh(); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to update cohort"); }
    } catch { toast.error("Failed to update cohort"); }
  };

  const addCohort = async (programId) => {
    try {
      const res = await fetch(`${API}/programs/${programId}/cohorts`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ start_date: "", schedule: "Mon-Fri, 9AM-5PM CST", max_participants: 15 })
      });
      if (res.ok) { toast.success("Cohort added"); onRefresh(); }
      else { toast.error("Failed to add cohort"); }
    } catch { toast.error("Failed to add cohort"); }
  };

  const deleteCohort = async (programId, cohortId) => {
    if (!window.confirm("Remove this cohort?")) return;
    try {
      const res = await fetch(`${API}/programs/${programId}/cohorts/${cohortId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Cohort removed"); onRefresh(); }
    } catch { toast.error("Failed to remove cohort"); }
  };

  const duplicateCohort = async (programId, cohortId) => {
    try {
      const res = await fetch(`${API}/programs/${programId}/cohorts/${cohortId}/duplicate`, { method: "POST", credentials: "include" });
      if (res.ok) { toast.success("Cohort duplicated — update the dates for the new term"); onRefresh(); }
      else { toast.error("Failed to duplicate cohort"); }
    } catch { toast.error("Failed to duplicate cohort"); }
  };

  return (
    <div className="space-y-4" data-testid="programs-tab">
      <div className="flex justify-between items-center">
        <p className="text-slate-500 text-sm">{programs.length} program{programs.length !== 1 ? "s" : ""}</p>
        <Button className="btn-primary" onClick={() => { setEditProgram(null); setDialogOpen(true); }} data-testid="create-program-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Program
        </Button>
      </div>

      <div className="space-y-4">
        {programs.map(prog => {
          const isExpanded = expandedProgram === prog.program_id;
          return (
            <Card key={prog.program_id} className="card-surface overflow-hidden" data-testid={`program-card-${prog.program_id}`}>
              <div className="flex items-start gap-4 p-4">
                {prog.photo_url ? (
                  <img src={prog.photo_url} alt={prog.name} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-[#1E3A8A]/20 to-[#FFD507]/10 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-8 h-8 text-brand-navy/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-slate-900 font-semibold">{prog.name}</h3>
                      <p className="text-brand-navy text-xs mt-0.5">{prog.title}</p>
                      <p className="text-slate-500 text-sm mt-1 line-clamp-1">{prog.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      {prog.duration_weeks && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">{prog.duration_weeks}w</Badge>}
                      <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">{prog.cohorts?.length || 0} cohorts</Badge>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" className="btn-outline"
                      onClick={() => { setEditProgram(prog); setDialogOpen(true); }} data-testid={`edit-program-${prog.program_id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="btn-outline"
                      onClick={() => setExpandedProgram(isExpanded ? null : prog.program_id)} data-testid={`toggle-cohorts-${prog.program_id}`}>
                      <CalendarDays className="w-3.5 h-3.5 mr-1" /> Cohorts
                      {isExpanded ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      onClick={() => handleDelete(prog.program_id)} data-testid={`delete-program-${prog.program_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Cohort Management (expandable) */}
              {isExpanded && (
                <div className="border-t border-slate-200 bg-slate-50/50 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-700">Cohorts & Schedules</h4>
                    <Button size="sm" className="btn-secondary" onClick={() => addCohort(prog.program_id)} data-testid={`add-cohort-${prog.program_id}`}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Cohort
                    </Button>
                  </div>
                  {(prog.cohorts || []).length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No cohorts yet</p>
                  ) : (
                    <div className="space-y-3">
                      {prog.cohorts.map((cohort) => (
                        <CohortEditor key={cohort.cohort_id} cohort={cohort} programId={prog.program_id}
                          onSave={saveCohort} onDelete={deleteCohort} onDuplicate={duplicateCohort} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
        {programs.length === 0 && (
          <div className="text-center py-16">
            <Layers className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">No programs yet</p>
          </div>
        )}
      </div>

      <ProgramFormDialog program={editProgram} open={dialogOpen} onClose={() => { setDialogOpen(false); setEditProgram(null); }} onSave={handleSave} />
    </div>
  );
};

// ============================================
// HELPERS
// ============================================
function getStatusStyle(status) {
  const styles = {
    draft: "bg-slate-500/20 text-slate-600 border-slate-500/30",
    intake_complete: "bg-blue-500/20 text-blue-300 border-blue-500/30",
    eligibility_complete: "bg-purple-500/20 text-purple-300 border-purple-500/30",
    application_complete: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    submitted: "bg-brand-gold/20 text-brand-navy border-[#FFD507]/30",
    under_review: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    approved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    rejected: "bg-red-500/20 text-red-300 border-red-500/30",
    waitlisted: "bg-purple-500/20 text-purple-300 border-purple-500/30",
    transferred: "bg-slate-400/20 text-slate-500 border-slate-400/30",
  };
  return styles[status] || "badge-gold";
}

// ============================================
// MAIN ADMIN PANEL
// ============================================
// ============================================
// WAITLIST MANAGEMENT TAB
// ============================================
const WaitlistTab = () => {
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [waitlistData, setWaitlistData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(null); // enrollment_id being assigned
  const [assignCohortId, setAssignCohortId] = useState("");

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await fetch(`${API}/programs?include_archived=true`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setPrograms(data.filter(p => p.is_active));
          if (data.length > 0 && !selectedProgramId) {
            setSelectedProgramId(data[0].program_id);
          }
        }
      } catch (err) { console.error(err); }
    };
    fetchPrograms();
  }, []);

  useEffect(() => {
    if (selectedProgramId) fetchWaitlist();
  }, [selectedProgramId]);

  const fetchWaitlist = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/programs/${selectedProgramId}/waitlist`, { credentials: "include" });
      if (res.ok) setWaitlistData(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleAssignCohort = async (enrollmentId) => {
    if (!assignCohortId) {
      toast.error("Please select a cohort");
      return;
    }
    try {
      const res = await fetch(`${API}/enrollment/${enrollmentId}/assign-cohort`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ cohort_id: assignCohortId }),
      });
      if (res.ok) {
        toast.success("Enrollee assigned to cohort!");
        setAssigning(null);
        setAssignCohortId("");
        fetchWaitlist();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to assign");
      }
    } catch { toast.error("Assignment failed"); }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatDateTime = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const cohorts = waitlistData?.program?.cohorts || [];
  const openCohorts = cohorts.filter(c => !c.max_participants || c.enrolled_count < c.max_participants);

  return (
    <div className="space-y-6" data-testid="waitlist-tab">
      {/* Program selector */}
      <Card className="card-surface">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <Label className="text-slate-600 font-medium whitespace-nowrap">Program:</Label>
            <Select value={selectedProgramId} onValueChange={setSelectedProgramId}>
              <SelectTrigger className="flex-1 bg-surface-muted border-slate-200" data-testid="waitlist-program-select">
                <SelectValue placeholder="Select a program" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {programs.map(p => (
                  <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#FFD507] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && waitlistData && (
        <>
          {/* Cohort overview */}
          <Card className="card-surface">
            <CardHeader>
              <CardTitle className="text-slate-900 text-base flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-[#1E3A8A]" />
                Cohort Capacity — {waitlistData.program.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cohorts.length === 0 ? (
                <p className="text-sm text-slate-500">No cohorts scheduled for this program.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {cohorts.map(c => {
                    const spots = (c.max_participants || 0) - (c.enrolled_count || 0);
                    const isFull = c.max_participants > 0 && spots <= 0;
                    return (
                      <div key={c.cohort_id} className={`p-3 rounded-lg border ${isFull ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
                        <p className="font-medium text-sm text-slate-800">{formatDate(c.start_date)}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-slate-500">{c.enrolled_count || 0} / {c.max_participants || "∞"} enrolled</span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isFull ? "text-red-700 bg-red-100" : "text-green-700 bg-green-100"}`}>
                            {isFull ? "Full" : `${spots} open`}
                          </span>
                        </div>
                        {c.schedule && <p className="text-xs text-slate-400 mt-1">{c.schedule}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Waitlist */}
          <Card className="card-surface">
            <CardHeader>
              <CardTitle className="text-slate-900 text-base flex items-center gap-2">
                <ListOrdered className="w-4 h-4 text-purple-600" />
                Waitlist ({waitlistData.total} enrollee{waitlistData.total !== 1 ? "s" : ""})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {waitlistData.total === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No one is on the waitlist for this program.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {waitlistData.waitlist.map((item) => (
                    <div key={item.enrollment_id} className="border border-slate-200 rounded-lg p-4 bg-white" data-testid={`waitlist-item-${item.enrollment_id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-sm flex-shrink-0">
                            #{item.waitlist_position}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-900">{item.user_name}</p>
                            <p className="text-sm text-slate-500">{item.user_email}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-slate-400">
                                Applied: {formatDateTime(item.applied_at)}
                              </span>
                              <Badge className={item.status === "waitlisted" ? "bg-purple-100 text-purple-800 border-purple-200" : "bg-amber-100 text-amber-800 border-amber-200"}>
                                {item.status === "waitlisted" ? "Waitlisted" : "Pending (Full)"}
                              </Badge>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {assigning === item.enrollment_id ? (
                            <div className="flex items-center gap-2">
                              <Select value={assignCohortId} onValueChange={setAssignCohortId}>
                                <SelectTrigger className="w-48 bg-white border-slate-200 h-9 text-sm" data-testid={`assign-cohort-select-${item.enrollment_id}`}>
                                  <SelectValue placeholder="Select cohort..." />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                  {openCohorts.length > 0 ? openCohorts.map(c => (
                                    <SelectItem key={c.cohort_id} value={c.cohort_id}>
                                      {formatDate(c.start_date)} ({(c.max_participants || 0) - (c.enrolled_count || 0)} spots)
                                    </SelectItem>
                                  )) : (
                                    <SelectItem value="none" disabled>All cohorts full</SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                onClick={() => handleAssignCohort(item.enrollment_id)}
                                disabled={!assignCohortId}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-3"
                                data-testid={`confirm-assign-${item.enrollment_id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" /> Assign
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setAssigning(null); setAssignCohortId(""); }} className="h-9 px-2 text-slate-400">
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setAssigning(item.enrollment_id); setAssignCohortId(""); }}
                              className="btn-outline h-9 flex items-center gap-1.5"
                              data-testid={`assign-btn-${item.enrollment_id}`}
                            >
                              <UserPlus className="w-4 h-4" /> Assign to Cohort
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ============================================
// BULK IMPORT TAB
// ============================================
const BulkImportTab = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) setFile(dropped);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/admin/bulk-import`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        toast.success(`Imported ${data.imported} users successfully!`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Import failed");
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-import-tab">
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#1E3A8A]" />
            Bulk Import Participants
          </CardTitle>
          <p className="text-sm text-slate-500">
            Upload an Excel (.xlsx) or CSV file to import multiple participants at once.
            Each row should contain participant data like name, email, program, etc.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${
              dragOver ? "border-[#FFD507] bg-[#FFD507]/5" : "border-slate-300 bg-slate-50"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            data-testid="import-drop-zone"
          >
            <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            {file ? (
              <div>
                <p className="text-slate-800 font-medium">{file.name}</p>
                <p className="text-sm text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                <button
                  onClick={() => setFile(null)}
                  className="text-sm text-red-500 hover:underline mt-2"
                  data-testid="remove-file-btn"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div>
                <p className="text-slate-600 font-medium">Drag & drop your file here</p>
                <p className="text-sm text-slate-400 mt-1">or</p>
                <label className="inline-block mt-3 cursor-pointer">
                  <span className="btn-primary text-sm px-4 py-2 rounded-lg">Browse Files</span>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="import-file-input"
                  />
                </label>
                <p className="text-xs text-slate-400 mt-3">Supported: .xlsx, .csv</p>
              </div>
            )}
          </div>

          {/* Expected columns info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Expected Columns
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-xs text-blue-700">
              {["className", "classDirectors", "FullName", "FirstName", "LastName", "Email", "CellPhone",
                "address1", "city", "state", "postalCode", "Date Of Birth", "Disability Status",
                "Gender", "Highest Education Level", "How did you find out about this program?"
              ].map(col => (
                <span key={col} className="bg-blue-100 px-2 py-1 rounded">{col}</span>
              ))}
            </div>
          </div>

          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="btn-primary w-full h-12 text-base flex items-center justify-center gap-2"
            data-testid="start-import-btn"
          >
            {uploading ? (
              <><div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" /> Importing...</>
            ) : (
              <><Upload className="w-5 h-5" /> Start Import</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card className="card-surface" data-testid="import-results">
          <CardHeader>
            <CardTitle className="text-slate-900">Import Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{result.imported}</p>
                <p className="text-sm text-green-600">Imported</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-yellow-700">{result.skipped}</p>
                <p className="text-sm text-yellow-600">Skipped</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-slate-700">{result.total_rows}</p>
                <p className="text-sm text-slate-500">Total Rows</p>
              </div>
            </div>

            {result.errors?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Skipped/Errors ({result.errors.length})</h4>
                <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 text-slate-600">Row</th>
                        <th className="text-left px-3 py-2 text-slate-600">Email</th>
                        <th className="text-left px-3 py-2 text-slate-600">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((err, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-500">{err.row}</td>
                          <td className="px-3 py-2 text-slate-600">{err.email || err.name || "-"}</td>
                          <td className="px-3 py-2 text-slate-500">{err.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.imported_users?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Sample Imported Users</h4>
                <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 text-slate-600">Name</th>
                        <th className="text-left px-3 py-2 text-slate-600">Email</th>
                        <th className="text-left px-3 py-2 text-slate-600">Program</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.imported_users.map((u, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-900">{u.name}</td>
                          <td className="px-3 py-2 text-slate-600">{u.email}</td>
                          <td className="px-3 py-2 text-slate-500">{u.program}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============================================
// FAQ ADMIN TAB
// ============================================
const FAQ_CATEGORIES = [
  "Eligibility & Who It's For",
  "Cost & Funding",
  "Schedule & Format",
  "Programs & Curriculum",
  "Enrollment & Application",
  "Apprenticeship & Employment"
];


// ============================================
// WORKING STYLE (CMS) ADMIN TAB
// ============================================
const WS_CATEGORIES = ["Communication", "Focus & Environment", "Feedback & Support", "Sensory Needs", "Collaboration", "Strengths"];
const WS_TYPES = [
  { value: "mcq", label: "Multiple choice (single)" },
  { value: "multi", label: "Multiple choice (multi)" },
  { value: "likert", label: "1-5 scale" },
  { value: "text", label: "Free-text" },
];


// ============================================
// COHORTS ADMIN TAB
// ============================================
const CohortsTab = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/programs`)
      .then(r => r.ok ? r.json() : [])
      .then(setPrograms)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-slate-500 text-center py-6">Loading cohorts...</p>;

  const allCohorts = [];
  programs.forEach(p => (p.cohorts || []).forEach(c => allCohorts.push({ ...c, program_name: p.name, program_id: p.program_id, duration_weeks: c.duration_weeks || p.duration_weeks })));
  allCohorts.sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));

  return (
    <div className="space-y-4" data-testid="cohorts-tab">
      <Card className="card-surface bg-gradient-to-br from-[#FFD507]/10 to-white border-2 border-[#FFD507]/30">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-base font-bold text-slate-900">📍 US Workforce Directory</h3>
              <p className="text-xs text-slate-500">Match participant zipcodes to their local Workforce Development Board.</p>
            </div>
            <Link to="/admin/workforce-directory">
              <Button className="btn-primary flex items-center gap-2" data-testid="open-workforce-btn">
                Open Workforce Directory →
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold text-slate-900">All 2026 Cohorts</h3>
          <p className="text-sm text-slate-500">{allCohorts.length} cohorts across {programs.length} programs</p>
        </div>
        <Link to="/instructor/classes">
          <Button variant="outline" className="flex items-center gap-2" data-testid="open-classes-btn">
            Class Sessions & Attendance →
          </Button>
        </Link>
      </div>

      <Card className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
              <th className="text-left p-3">Start</th>
              <th className="text-left p-3">Program</th>
              <th className="text-left p-3">Duration</th>
              <th className="text-left p-3">Instructor(s)</th>
              <th className="text-left p-3">Capacity</th>
              <th className="text-left p-3">Notes</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {allCohorts.map(c => (
              <tr key={c.cohort_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`cohort-row-${c.cohort_id}`}>
                <td className="p-3 font-semibold text-slate-900">{c.start_date}</td>
                <td className="p-3 text-slate-700">{c.program_name}</td>
                <td className="p-3 text-slate-600">{c.duration_weeks}wk</td>
                <td className="p-3 text-slate-600">{(c.instructors || []).join(", ") || "—"}</td>
                <td className="p-3 text-slate-600">{c.min_participants}-{c.max_participants}</td>
                <td className="p-3 text-slate-500 text-xs">{c.notes || "—"}</td>
                <td className="p-3 text-right">
                  <Link to={`/admin/cohorts/${c.cohort_id}/roster`}>
                    <Button size="sm" variant="outline" data-testid={`view-cohort-${c.cohort_id}`}>View Roster →</Button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};


const WorkingStyleAdminTab = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ category: WS_CATEGORIES[0], text: "", q_type: "mcq", options: [], sort_order: 0, is_active: true });
  const [newOption, setNewOption] = useState("");

  const fetchQuestions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/working-style/questions`, { credentials: "include" });
      if (res.ok) setQuestions(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const handleSave = async () => {
    const url = editing
      ? `${API}/admin/working-style/questions/${editing.question_id}`
      : `${API}/admin/working-style/questions`;
    const method = editing ? "PUT" : "POST";
    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(editing ? "Question updated" : "Question created");
        setEditing(null);
        setCreating(false);
        setForm({ category: WS_CATEGORIES[0], text: "", q_type: "mcq", options: [], sort_order: 0, is_active: true });
        fetchQuestions();
      } else {
        toast.error("Failed to save");
      }
    } catch { toast.error("An error occurred"); }
  };

  const handleDelete = async (qid) => {
    if (!window.confirm("Delete this question?")) return;
    const res = await fetch(`${API}/admin/working-style/questions/${qid}`, { method: "DELETE", credentials: "include" });
    if (res.ok) {
      toast.success("Question deleted");
      fetchQuestions();
    } else {
      toast.error("Failed to delete");
    }
  };

  const startEdit = (q) => {
    setEditing(q);
    setCreating(false);
    setForm({ category: q.category, text: q.text, q_type: q.q_type, options: q.options || [], sort_order: q.sort_order || 0, is_active: q.is_active !== false });
  };

  const startCreate = () => {
    setEditing(null);
    setCreating(true);
    setForm({ category: WS_CATEGORIES[0], text: "", q_type: "mcq", options: [], sort_order: questions.length, is_active: true });
  };

  const addOption = () => {
    if (!newOption.trim()) return;
    setForm((f) => ({ ...f, options: [...f.options, newOption.trim()] }));
    setNewOption("");
  };

  const removeOption = (idx) => {
    setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));
  };

  const grouped = questions.reduce((acc, q) => {
    (acc[q.category] = acc[q.category] || []).push(q);
    return acc;
  }, {});

  if (loading) return <div className="text-center py-8 text-slate-500">Loading Working Style questions...</div>;

  const isEditing = editing || creating;
  const needsOptions = form.q_type === "mcq" || form.q_type === "multi";

  return (
    <div className="space-y-4" data-testid="working-style-admin-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-bold text-slate-900 flex items-center gap-2">
            <Rocket className="w-5 h-5 text-[#1E3A8A]" /> Working Style Questions
          </h3>
          <p className="text-sm text-slate-500">{questions.length} total questions across {Object.keys(grouped).length} categories</p>
        </div>
        <Button onClick={startCreate} className="btn-primary" data-testid="add-ws-question-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Question
        </Button>
      </div>

      {isEditing && (
        <Card className="card-surface border-[#FFD507]/40">
          <CardHeader>
            <CardTitle className="text-slate-900 text-base">{editing ? "Edit Question" : "New Question"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-600">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger className="input-base" data-testid="ws-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{WS_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Type</Label>
                <Select value={form.q_type} onValueChange={(v) => setForm((f) => ({ ...f, q_type: v, options: (v === "likert" || v === "text") ? [] : f.options }))}>
                  <SelectTrigger className="input-base" data-testid="ws-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{WS_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600">Question text</Label>
              <Textarea value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} className="input-base min-h-[80px]" data-testid="ws-text-input" />
            </div>
            {needsOptions && (
              <div className="space-y-2">
                <Label className="text-slate-600">Answer options</Label>
                <div className="flex gap-2">
                  <Input value={newOption} onChange={(e) => setNewOption(e.target.value)} placeholder="Add an option..." className="input-base" onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addOption())} data-testid="ws-option-input" />
                  <Button type="button" onClick={addOption} className="btn-secondary"><Plus className="w-4 h-4" /></Button>
                </div>
                {form.options.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {form.options.map((opt, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                        {opt}
                        <button type="button" onClick={() => removeOption(idx)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-600">Sort order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 0 }))} className="input-base" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input id="ws-active" type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
                <Label htmlFor="ws-active" className="text-slate-600">Active (visible to participants)</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setEditing(null); setCreating(false); }}>Cancel</Button>
              <Button onClick={handleSave} className="btn-primary" data-testid="ws-save-btn">Save</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {Object.entries(grouped).map(([cat, qs]) => (
        <Card key={cat} className="card-surface">
          <CardHeader><CardTitle className="text-slate-900 text-base">{cat}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {qs.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((q) => (
              <div key={q.question_id} className="flex items-start justify-between gap-4 p-3 rounded-lg border border-slate-200 hover:border-[#FFD507]/40" data-testid={`ws-q-${q.question_id}`}>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">{q.text}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {WS_TYPES.find((t) => t.value === q.q_type)?.label} · Order {q.sort_order}
                    {!q.is_active && <span className="ml-2 text-orange-600">(inactive)</span>}
                    {q.options?.length > 0 && <span className="ml-2">· {q.options.length} options</span>}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(q)} data-testid={`ws-edit-${q.question_id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(q.question_id)} data-testid={`ws-delete-${q.question_id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};



// ============================================
// MENTOR APPLICATIONS ADMIN TAB
// ============================================
const MENTOR_STATUSES = [
  { value: "pending", label: "Pending", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "approved", label: "Approved", color: "bg-green-100 text-green-700 border-green-200" },
  { value: "rejected", label: "Rejected", color: "bg-rose-100 text-rose-700 border-rose-200" },
  { value: "onboarded", label: "Onboarded", color: "bg-blue-100 text-blue-700 border-blue-200" },
];

const MentorApplicationsTab = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchApps = useCallback(async () => {
    try {
      const url = filter === "all"
        ? `${API}/admin/mentor-applications`
        : `${API}/admin/mentor-applications?status=${filter}`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) setApps(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const handleStatusChange = async (id, newStatus) => {
    const res = await fetch(`${API}/admin/mentor-applications/${id}/status?new_status=${newStatus}`, {
      method: "PUT",
      credentials: "include",
    });
    if (res.ok) {
      toast.success(`Status updated to ${newStatus}`);
      fetchApps();
    } else {
      toast.error("Failed to update status");
    }
  };

  const counts = MENTOR_STATUSES.reduce((acc, s) => {
    acc[s.value] = apps.filter(a => a.status === s.value).length;
    return acc;
  }, {});

  if (loading) return <div className="text-center py-8 text-slate-500">Loading mentor applications...</div>;

  const filteredApps = filter === "all" ? apps : apps.filter(a => a.status === filter);

  return (
    <div className="space-y-4" data-testid="mentor-applications-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-heading font-bold text-slate-900 flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#1E3A8A]" /> Mentor Applications
          </h3>
          <p className="text-sm text-slate-500">{apps.length} total applications</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("all")}
            className={filter === "all" ? "btn-primary" : ""}
            data-testid="mentor-filter-all"
          >
            All ({apps.length})
          </Button>
          {MENTOR_STATUSES.map(s => (
            <Button
              key={s.value}
              variant={filter === s.value ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(s.value)}
              className={filter === s.value ? "btn-primary" : ""}
              data-testid={`mentor-filter-${s.value}`}
            >
              {s.label} ({counts[s.value] || 0})
            </Button>
          ))}
        </div>
      </div>

      {filteredApps.length === 0 ? (
        <Card className="card-surface">
          <CardContent className="py-12 text-center text-slate-500">
            <Heart className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p>No mentor applications {filter !== "all" ? `with status "${filter}"` : "yet"}.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredApps.map((app) => {
            const statusInfo = MENTOR_STATUSES.find(s => s.value === app.status) || MENTOR_STATUSES[0];
            return (
              <Card key={app.application_id} className="card-surface" data-testid={`mentor-app-${app.application_id}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px] space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h4 className="font-heading font-bold text-slate-900">{app.name}</h4>
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500">{app.email}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                        <span><strong className="text-slate-700">Field:</strong> {app.field_of_expertise}</span>
                        {app.years_experience && (
                          <span><strong className="text-slate-700">Experience:</strong> {app.years_experience}</span>
                        )}
                        {app.current_role && (
                          <span><strong className="text-slate-700">Role:</strong> {app.current_role}</span>
                        )}
                        {(app.city || app.state) && (
                          <span><strong className="text-slate-700">Location:</strong> {[app.city, app.state].filter(Boolean).join(", ")}</span>
                        )}
                        {app.linkedin_url && (
                          <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#0A66C2] hover:underline">
                            LinkedIn ↗
                          </a>
                        )}
                      </div>
                      {(app.communication_preference || app.availability || app.personal_interests) && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                          {app.communication_preference && (
                            <span><strong className="text-slate-700">Prefers:</strong> {app.communication_preference}</span>
                          )}
                          {app.availability && (
                            <span><strong className="text-slate-700">Availability:</strong> {app.availability}</span>
                          )}
                          {app.personal_interests && (
                            <span><strong className="text-slate-700">Interests:</strong> {app.personal_interests}</span>
                          )}
                        </div>
                      )}
                      {app.areas_of_interest?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {app.areas_of_interest.map((i) => (
                            <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A]">{i}</span>
                          ))}
                        </div>
                      )}
                      <p className="text-sm text-slate-700 italic border-l-2 border-[#FFD507]/30 pl-3 mt-2">
                        "{app.why_mentor}"
                      </p>
                      <p className="text-xs text-slate-400">Applied {new Date(app.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {app.status === "pending" && (
                        <>
                          <Button size="sm" onClick={() => handleStatusChange(app.application_id, "approved")} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-${app.application_id}`}>Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => handleStatusChange(app.application_id, "rejected")} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`reject-${app.application_id}`}>Reject</Button>
                        </>
                      )}
                      {app.status === "approved" && (
                        <Button size="sm" onClick={() => handleStatusChange(app.application_id, "onboarded")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`onboard-${app.application_id}`}>Mark Onboarded</Button>
                      )}
                      {(app.status === "rejected" || app.status === "onboarded") && (
                        <Button size="sm" variant="outline" onClick={() => handleStatusChange(app.application_id, "pending")} data-testid={`reopen-${app.application_id}`}>Reopen</Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};


const FAQAdminTab = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState({ category: FAQ_CATEGORIES[0], question: "", answer: "", sort_order: 0, is_active: true });

  const fetchFaqs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/faqs`, { credentials: "include" });
      if (res.ok) setFaqs(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  const handleSave = async () => {
    const url = editingFaq 
      ? `${API}/admin/faqs/${editingFaq.faq_id}` 
      : `${API}/admin/faqs`;
    const method = editingFaq ? "PUT" : "POST";
    
    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(editingFaq ? "FAQ updated!" : "FAQ created!");
        setEditingFaq(null);
        setIsCreating(false);
        setForm({ category: FAQ_CATEGORIES[0], question: "", answer: "", sort_order: 0, is_active: true });
        fetchFaqs();
      }
    } catch (e) { toast.error("Failed to save FAQ"); }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      const res = await fetch(`${API}/admin/faqs/${faqId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        toast.success("FAQ deleted");
        fetchFaqs();
      }
    } catch (e) { toast.error("Failed to delete"); }
  };

  const startEdit = (faq) => {
    setEditingFaq(faq);
    setIsCreating(false);
    setForm({ category: faq.category, question: faq.question, answer: faq.answer, sort_order: faq.sort_order, is_active: faq.is_active });
  };

  const startCreate = () => {
    setEditingFaq(null);
    setIsCreating(true);
    setForm({ category: FAQ_CATEGORIES[0], question: "", answer: "", sort_order: 0, is_active: true });
  };

  const grouped = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) acc[faq.category] = [];
    acc[faq.category].push(faq);
    return acc;
  }, {});

  if (loading) return <div className="text-center py-8 text-slate-500">Loading FAQs...</div>;

  return (
    <div className="space-y-4" data-testid="faq-admin-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-bold text-slate-900">FAQ Management</h3>
          <p className="text-sm text-slate-500">{faqs.length} total FAQs across {Object.keys(grouped).length} categories</p>
        </div>
        <Button onClick={startCreate} className="btn-primary" data-testid="create-faq-btn">
          <Plus className="w-4 h-4 mr-2" /> Add FAQ
        </Button>
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingFaq) && (
        <Card className="card-light border-[#FFD507]/30">
          <CardContent className="py-4 space-y-3">
            <h4 className="font-semibold text-sm text-slate-800">{editingFaq ? "Edit FAQ" : "New FAQ"}</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger className="input-base mt-1" data-testid="faq-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {FAQ_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Sort Order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: parseInt(e.target.value) || 0})} className="input-base mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Question</Label>
              <Input value={form.question} onChange={(e) => setForm({...form, question: e.target.value})} className="input-base mt-1" data-testid="faq-question-input" placeholder="Enter the question..." />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Answer</Label>
              <textarea value={form.answer} onChange={(e) => setForm({...form, answer: e.target.value})} rows={4} className="w-full input-base mt-1 resize-y" data-testid="faq-answer-input" placeholder="Enter the answer..." />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.checked})} className="rounded" />
                Active (visible on FAQ page)
              </label>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button onClick={handleSave} className="btn-primary" data-testid="save-faq-btn">
                {editingFaq ? "Update" : "Create"} FAQ
              </Button>
              <Button variant="outline" onClick={() => { setEditingFaq(null); setIsCreating(false); }} className="border-slate-200">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* FAQ List by Category */}
      {Object.entries(grouped).map(([category, items]) => (
        <Card key={category} className="card-light">
          <CardContent className="py-3">
            <h4 className="font-semibold text-sm text-slate-700 mb-2">{category} ({items.length})</h4>
            <div className="space-y-2">
              {items.sort((a, b) => a.sort_order - b.sort_order).map(faq => (
                <div key={faq.faq_id} className={`flex items-start justify-between p-3 rounded-lg border ${faq.is_active ? 'bg-white border-slate-100' : 'bg-slate-50 border-slate-200 opacity-60'}`} data-testid={`faq-admin-item-${faq.faq_id}`}>
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-medium text-slate-800 truncate">{faq.question}</p>
                    <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{faq.answer}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!faq.is_active && <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Hidden</span>}
                    <button onClick={() => startEdit(faq)} className="p-1.5 hover:bg-slate-100 rounded" title="Edit">
                      <Pencil className="w-3.5 h-3.5 text-slate-500" />
                    </button>
                    <button onClick={() => handleDelete(faq.faq_id)} className="p-1.5 hover:bg-red-50 rounded" title="Delete">
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};




// ============================================
// SECURITY: Audit Log Viewer + Data Management
// ============================================
const ACTION_COLORS = {
  login_success: "bg-emerald-100 text-emerald-700",
  login_failed: "bg-red-100 text-red-700",
  enrollment_status_change: "bg-blue-100 text-blue-700",
  faq_create: "bg-amber-100 text-amber-700",
  faq_update: "bg-amber-100 text-amber-700",
  faq_delete: "bg-red-100 text-red-700",
  data_export: "bg-purple-100 text-purple-700",
  data_purge: "bg-red-100 text-red-800",
};

const SecurityTab = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [actions, setActions] = useState([]);
  const [exportUserId, setExportUserId] = useState("");
  const [purgeUserId, setPurgeUserId] = useState("");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/audit-logs?page=${page}&limit=30`;
      if (actionFilter) url += `&action=${actionFilter}`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs);
        setTotal(data.total);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, actionFilter]);

  const fetchActions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/audit-logs/actions`, { credentials: "include" });
      if (res.ok) setActions(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchActions(); }, [fetchActions]);

  const handleExport = async () => {
    if (!exportUserId.trim()) return;
    try {
      const res = await fetch(`${API}/admin/data-export/${exportUserId.trim()}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `user_data_export_${exportUserId.trim()}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Data exported successfully");
        setExportUserId("");
        fetchLogs();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Export failed");
      }
    } catch (e) { toast.error("Export failed"); }
  };

  const handlePurge = async () => {
    if (!purgeUserId.trim()) return;
    if (!window.confirm(`PERMANENT ACTION: This will delete ALL data for user ${purgeUserId.trim()}. This cannot be undone. Continue?`)) return;
    if (!window.confirm("Are you absolutely sure? This is irreversible.")) return;
    try {
      const res = await fetch(`${API}/admin/data-purge/${purgeUserId.trim()}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        toast.success("User data purged");
        setPurgeUserId("");
        fetchLogs();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Purge failed");
      }
    } catch (e) { toast.error("Purge failed"); }
  };

  return (
    <div className="space-y-6" data-testid="security-tab">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#0F1B3D] flex items-center justify-center">
          <Shield className="w-5 h-5 text-[#1E3A8A]" />
        </div>
        <div>
          <h3 className="text-lg font-heading font-bold text-slate-900">Security & Compliance</h3>
          <p className="text-sm text-slate-500">Audit trail, data management, and security tools</p>
        </div>
      </div>

      {/* Data Management Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="card-light">
          <CardContent className="py-4 space-y-3">
            <h4 className="font-semibold text-sm text-slate-800 flex items-center gap-2">
              <Download className="w-4 h-4 text-blue-600" /> Data Export (GDPR)
            </h4>
            <p className="text-xs text-slate-500">Export all data for a specific user. Downloads as JSON.</p>
            <div className="flex gap-2">
              <Input 
                value={exportUserId} onChange={(e) => setExportUserId(e.target.value)} 
                placeholder="Enter user_id..." className="input-base text-sm" 
                data-testid="export-user-id-input"
              />
              <Button onClick={handleExport} className="btn-primary text-xs px-4" disabled={!exportUserId.trim()} data-testid="export-user-btn">
                Export
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="card-light border-red-100">
          <CardContent className="py-4 space-y-3">
            <h4 className="font-semibold text-sm text-red-700 flex items-center gap-2">
              <Trash2 className="w-4 h-4" /> Data Purge (Right to Erasure)
            </h4>
            <p className="text-xs text-slate-500">Permanently delete all data for a user. This cannot be undone.</p>
            <div className="flex gap-2">
              <Input 
                value={purgeUserId} onChange={(e) => setPurgeUserId(e.target.value)} 
                placeholder="Enter user_id..." className="input-base text-sm" 
                data-testid="purge-user-id-input"
              />
              <Button onClick={handlePurge} variant="destructive" className="text-xs px-4" disabled={!purgeUserId.trim()} data-testid="purge-user-btn">
                Purge
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Audit Log */}
      <Card className="card-light">
        <CardContent className="py-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm text-slate-800">Audit Trail ({total} events)</h4>
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="input-base w-48" data-testid="audit-action-filter"><SelectValue placeholder="Filter by action" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="all">All Actions</SelectItem>
                {actions.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading audit logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No audit events found.</div>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => (
                <div key={log.audit_id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 bg-white hover:bg-slate-50/50 transition-colors" data-testid={`audit-log-${log.audit_id}`}>
                  <div className="flex-shrink-0 mt-0.5">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ACTION_COLORS[log.action] || "bg-slate-100 text-slate-600"}`}>
                      {log.action.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-700">
                      <span className="font-medium">{log.actor_email}</span>
                      {log.target_type && <span className="text-slate-400"> on {log.target_type}</span>}
                      {log.target_id && <span className="text-slate-400 font-mono text-[10px]"> ({log.target_id.length > 16 ? log.target_id.slice(0, 16) + "..." : log.target_id})</span>}
                    </p>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <p className="text-[10px] text-slate-400 mt-0.5 truncate">
                        {Object.entries(log.details).slice(0, 3).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(" | ")}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[10px] text-slate-400">{new Date(log.timestamp).toLocaleDateString()}</p>
                    <p className="text-[10px] text-slate-400">{new Date(log.timestamp).toLocaleTimeString()}</p>
                    <p className="text-[10px] text-slate-300 font-mono">{log.ip_address}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > 30 && (
            <div className="flex items-center justify-center gap-2 pt-3">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="text-xs">
                Previous
              </Button>
              <span className="text-xs text-slate-500">Page {page} of {Math.ceil(total / 30)}</span>
              <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 30)} onClick={() => setPage(p => p + 1)} className="text-xs">
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


const AdminPanel = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Restore last-viewed tab (per-admin) and respect ?tab= query param
    const url = new URLSearchParams(window.location.search);
    const fromUrl = url.get("tab");
    if (fromUrl) return fromUrl;
    try {
      return localStorage.getItem("admin-active-tab") || "overview";
    } catch (_) {
      return "overview";
    }
  });

  // Persist active tab so admins don't lose context on refresh
  useEffect(() => {
    try { localStorage.setItem("admin-active-tab", activeTab); } catch (_) { /* ignore */ }
  }, [activeTab]);

  const [cohortAssignUser, setCohortAssignUser] = useState(null);
  const [assignProgramId, setAssignProgramId] = useState("");
  const [assignCohortId, setAssignCohortId] = useState("");
  // Add-user modal
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "participant" });
  const [creatingUser, setCreatingUser] = useState(false);
  const [siteContent, setSiteContent] = useState(null);
  const [siteContentDirty, setSiteContentDirty] = useState(false);
  const [savingSiteContent, setSavingSiteContent] = useState(false);
  // Course creation state removed - now using CourseEditor page

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, pipelineRes, coursesRes, programsRes] = await Promise.all([
        fetch(`${API}/stats/admin`, { credentials: "include" }),
        fetch(`${API}/users`, { credentials: "include" }),
        fetch(`${API}/enrollments/pipeline`, { credentials: "include" }),
        fetch(`${API}/courses?active_only=false`, { credentials: "include" }),
        fetch(`${API}/programs?active_only=false`, { credentials: "include" }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (pipelineRes.ok) setEnrollments(await pipelineRes.json());
      if (coursesRes.ok) setCourses(await coursesRes.json());
      if (programsRes.ok) setPrograms(await programsRes.json());
      // Fetch site content
      const siteRes = await fetch(`${API}/site-content`, { credentials: "include" });
      if (siteRes.ok) setSiteContent(await siteRes.json());
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateEnrollmentStatus = async (enrollmentId, status) => {
    try {
      const res = await fetch(`${API}/enrollments/${enrollmentId}/status?status=${status}`, {
        method: "PUT", credentials: "include"
      });
      if (res.ok) { toast.success(`Enrollment ${status}`); fetchData(); }
    } catch { toast.error("Failed to update status"); }
  };

  const updateUserRole = async (userId, role) => {
    try {
      const res = await fetch(`${API}/users/${userId}/role?role=${role}`, { method: "PUT", credentials: "include" });
      if (res.ok) { toast.success("User role updated"); fetchData(); }
    } catch { toast.error("Failed to update role"); }
  };

  const updateFundingSource = async (userId, fundingSource) => {
    try {
      const res = await fetch(`${API}/admin/user/${userId}/funding-source`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ funding_source: fundingSource }),
      });
      if (!res.ok) throw new Error();
      toast.success(fundingSource ? "Funding source tagged" : "Funding source cleared");
      fetchData();
    } catch { toast.error("Failed to update funding source"); }
  };

  const createUser = async () => {
    const name = newUser.name.trim();
    const email = newUser.email.trim();
    if (!name || !email) { toast.error("Name and email are required"); return; }
    setCreatingUser(true);
    try {
      const res = await fetch(`${API}/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name, email, role: newUser.role }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Could not create user");
      toast.success(`${name} added as ${newUser.role}`);
      if (data.dev_url) {
        window.prompt(`DEV/PREVIEW MODE — copy this password-setup link to share with ${email}:`, data.dev_url);
      }
      setAddUserOpen(false);
      setNewUser({ name: "", email: "", role: "participant" });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreatingUser(false);
    }
  };

  const setUserActive = async (u, isActive) => {
    if (!isActive && !window.confirm(`Deactivate ${u.email}? They will be logged out and blocked from signing in until reactivated.`)) return;
    try {
      const res = await fetch(`${API}/admin/users/${u.user_id}/active`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: isActive }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Update failed");
      toast.success(isActive ? `${u.email} reactivated` : `${u.email} deactivated`);
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const deleteUserPermanent = async (u) => {
    if (!window.confirm(`PERMANENTLY DELETE ${u.email}?\n\nThis removes the user and ALL their data (enrollments, documents, notes). This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/data-purge/${u.user_id}`, { method: "DELETE", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Delete failed");
      toast.success(`${u.email} permanently deleted`);
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const toggleAlumniStatus = async (userId, isAlumni) => {
    try {
      const res = await fetch(`${API}/users/${userId}/alumni?is_alumni=${isAlumni}`, { method: "PUT", credentials: "include" });
      if (res.ok) { toast.success(`Alumni status ${isAlumni ? "granted" : "revoked"}`); fetchData(); }
    } catch { toast.error("Failed to update"); }
  };

  const updatePostTrainingStatus = async (userId, statusData) => {
    try {
      const res = await fetch(`${API}/admin/user/${userId}/post-training-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(statusData),
      });
      if (res.ok) { toast.success("Post-training status updated"); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to update"); }
    } catch { toast.error("Failed to update"); }
  };

  const updateCertification = async (userId, isCertified) => {
    try {
      const res = await fetch(`${API}/admin/user/${userId}/certification`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_certified: isCertified }),
      });
      if (res.ok) { toast.success(`Certification ${isCertified ? "approved" : "revoked"}`); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to update"); }
    } catch { toast.error("Failed to update"); }
  };

  const assignCohort = async () => {
    if (!cohortAssignUser || !assignProgramId || !assignCohortId) return;
    try {
      const res = await fetch(`${API}/admin/users/${cohortAssignUser.user_id}/assign-cohort`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ program_id: assignProgramId, cohort_id: assignCohortId }),
      });
      if (res.ok) {
        toast.success("Instructor assigned to cohort");
        setCohortAssignUser(null);
        setAssignProgramId("");
        setAssignCohortId("");
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to assign");
      }
    } catch { toast.error("Failed to assign"); }
  };

  const saveSiteContent = async () => {
    setSavingSiteContent(true);
    try {
      const res = await fetch(`${API}/site-content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(siteContent),
      });
      if (res.ok) {
        toast.success("Site content saved! Changes are live.");
        setSiteContentDirty(false);
      } else {
        toast.error("Failed to save site content");
      }
    } catch { toast.error("Failed to save"); }
    finally { setSavingSiteContent(false); }
  };

  const updateContent = (path, value) => {
    setSiteContent(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = isNaN(keys[i]) ? keys[i] : parseInt(keys[i]);
        obj = obj[k];
      }
      const lastKey = isNaN(keys[keys.length - 1]) ? keys[keys.length - 1] : parseInt(keys[keys.length - 1]);
      obj[lastKey] = value;
      return updated;
    });
    setSiteContentDirty(true);
  };

  const handleViewDetail = async (enrollment) => {
    try {
      const res = await fetch(`${API}/enrollments/${enrollment.enrollment_id}/detail`, { credentials: "include" });
      if (res.ok) {
        setSelectedEnrollment(await res.json());
        setDetailOpen(true);
      }
    } catch { toast.error("Failed to load details"); }
  };

  if (loading) {
    return (
      <PageLayout title="Admin Panel">
        <div className="flex items-center justify-center py-20"><div className="spinner" /></div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Admin Panel" subtitle="Manage enrollment pipeline, programs, materials, and users.">
      <div className="lg:grid lg:grid-cols-[240px,1fr] lg:gap-8" data-testid="admin-panel">
        <AdminSidebar active={activeTab} onChange={setActiveTab} />

        <div className="min-w-0 space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            {/* Hidden TabsList — sidebar drives the value, but Radix Tabs needs a TabsList present for accessibility */}
            <TabsList className="sr-only">
              <TabsTrigger value="overview" />
              <TabsTrigger value="pipeline" />
              <TabsTrigger value="enrollees" />
              <TabsTrigger value="programs" />
              <TabsTrigger value="users" />
              <TabsTrigger value="courses" />
              <TabsTrigger value="cohorts" />
              <TabsTrigger value="site-content" />
              <TabsTrigger value="welcome-pack" />
              <TabsTrigger value="contact-page" />
              <TabsTrigger value="stories" />
              <TabsTrigger value="bulk-import" />
              <TabsTrigger value="waitlist" />
              <TabsTrigger value="state-waitlist" />
              <TabsTrigger value="faqs" />
              <TabsTrigger value="mentors" />
              <TabsTrigger value="working-style" />
              <TabsTrigger value="security" />
              <TabsTrigger value="maintenance" />
              <TabsTrigger value="instructor-library" />
              <TabsTrigger value="apprenticeship" />
              <TabsTrigger value="architecture" />
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <AdminOverview onNavigate={setActiveTab} />
            </TabsContent>

            {/* Apprenticeship Compliance Tab */}
            <TabsContent value="apprenticeship">
              <ApprenticeshipTab />
            </TabsContent>

            {/* System Architecture Tab */}
            <TabsContent value="architecture">
              <ArchitectureTab />
            </TabsContent>

            {/* Pipeline Tab */}
            <TabsContent value="pipeline">
            <div className="space-y-4">
              {user?.role === "admin" && <StudentLocationsMap />}
              {user?.role === "admin" && <DropboxConnectCard />}
              <PipelineTab enrollments={enrollments} onStatusChange={updateEnrollmentStatus} onViewDetail={handleViewDetail} />
            </div>
          </TabsContent>

          {/* Enrollees Tab */}
          <TabsContent value="enrollees" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input placeholder="Search enrollees..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="input-base pl-10" data-testid="enrollee-search-input" />
            </div>
            <Card className="card-surface">
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-200">
                        <TableHead className="text-slate-500">Health</TableHead>
                        <TableHead className="text-slate-500">Applicant</TableHead>
                        <TableHead className="text-slate-500">Email</TableHead>
                        <TableHead className="text-slate-500">Step</TableHead>
                        <TableHead className="text-slate-500">Status</TableHead>
                        <TableHead className="text-slate-500">Date</TableHead>
                        <TableHead className="text-slate-500">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enrollments
                        .filter(e => {
                          const term = searchTerm.toLowerCase();
                          return !term || (e.applicant_name || "").toLowerCase().includes(term) || (e.user_email || "").toLowerCase().includes(term);
                        })
                        .map(enrollment => (
                          <TableRow key={enrollment.enrollment_id} className="border-slate-200" data-testid={`enrollee-row-${enrollment.enrollment_id}`}>
                            <TableCell><HealthDot health={enrollment.health} testid={`enrollee-health-${enrollment.enrollment_id}`} /></TableCell>
                            <TableCell className="text-slate-900 font-medium">{enrollment.applicant_name || enrollment.user_name}</TableCell>
                            <TableCell className="text-slate-600">{enrollment.user_email}</TableCell>
                            <TableCell className="text-slate-500">{enrollment.current_step}/3</TableCell>
                            <TableCell>
                              <Badge className={getStatusStyle(enrollment.status)}>
                                {enrollment.status?.replace(/_/g, " ")}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-slate-500">
                              {new Date(enrollment.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="btn-outline h-8"
                                  onClick={() => handleViewDetail(enrollment)} data-testid={`view-enrollee-${enrollment.enrollment_id}`}>
                                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                                </Button>
                                {enrollment.status === "submitted" && (
                                  <>
                                    <Button size="sm" className="h-8 bg-[#10B981] hover:bg-[#059669] text-slate-900"
                                      onClick={() => updateEnrollmentStatus(enrollment.enrollment_id, "approved")}>
                                      <CheckCircle className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button size="sm" className="h-8 bg-[#EF4444] hover:bg-[#DC2626] text-slate-900"
                                      onClick={() => updateEnrollmentStatus(enrollment.enrollment_id, "rejected")}>
                                      <XCircle className="w-3.5 h-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      {enrollments.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-slate-500 py-8">No enrollments yet</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Programs Tab */}
          <TabsContent value="programs">
            <ProgramsTab programs={programs} onRefresh={fetchData} />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input placeholder="Search users..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="input-base pl-10" data-testid="user-search-input" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48 bg-surface-muted border-slate-200" data-testid="user-status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="no_status">No Status Set</SelectItem>
                  <SelectItem value="Internship">Internship</SelectItem>
                  <SelectItem value="Apprenticeship">Apprenticeship</SelectItem>
                  <SelectItem value="On the Job Training">On the Job Training</SelectItem>
                  <SelectItem value="Employment">Employment</SelectItem>
                  <SelectItem value="Seeking Employment">Seeking Employment</SelectItem>
                  <SelectItem value="Ongoing Education">Ongoing Education</SelectItem>
                </SelectContent>
              </Select>
              <Button className="btn-primary gap-2 shrink-0" onClick={() => setAddUserOpen(true)} data-testid="add-user-btn">
                <UserPlus className="w-4 h-4" /> Add User
              </Button>
            </div>
            <Card className="card-surface">
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-200">
                        <TableHead className="text-slate-500">Name</TableHead>
                        <TableHead className="text-slate-500">Email</TableHead>
                        <TableHead className="text-slate-500">Role</TableHead>
                        <TableHead className="text-slate-500">Funding</TableHead>
                        <TableHead className="text-slate-500">Post-Training Status</TableHead>
                        <TableHead className="text-slate-500">Certified</TableHead>
                        <TableHead className="text-slate-500">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users
                        .filter(u => {
                          const term = searchTerm.toLowerCase();
                          const matchesSearch = !term || u.name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term);
                          const matchesFilter = statusFilter === "all"
                            || (statusFilter === "no_status" && !u.post_training_status)
                            || u.post_training_status === statusFilter;
                          return matchesSearch && matchesFilter;
                        })
                        .map(u => (
                          <TableRow key={u.user_id} className="border-slate-200" data-testid={`user-row-${u.user_id}`}>
                            <TableCell>
                              <div>
                                <p className="text-slate-900 font-medium flex items-center gap-2">
                                  {u.name}
                                  {u.is_active === false && (
                                    <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded" data-testid={`inactive-badge-${u.user_id}`}>Inactive</span>
                                  )}
                                </p>
                                {u.post_training_employer && (
                                  <p className="text-xs text-slate-400">{u.post_training_employer}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-600">{u.email}</TableCell>
                            <TableCell>
                              <Select defaultValue={u.role} onValueChange={value => updateUserRole(u.user_id, value)}>
                                <SelectTrigger className="w-32 bg-surface-muted border-slate-200"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                  <SelectItem value="participant">Participant</SelectItem>
                                  <SelectItem value="instructor">Instructor</SelectItem>
                                  <SelectItem value="mentor">Mentor</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Select value={u.funding_source || "none"} onValueChange={value => updateFundingSource(u.user_id, value === "none" ? "" : value)}>
                                <SelectTrigger className="w-32 bg-surface-muted border-slate-200" data-testid={`funding-select-${u.user_id}`}>
                                  <SelectValue placeholder="Untagged" />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                  <SelectItem value="none">Untagged</SelectItem>
                                  <SelectItem value="wioa">WIOA</SelectItem>
                                  <SelectItem value="vr_drs">VR / DRS</SelectItem>
                                  <SelectItem value="grant">Grant</SelectItem>
                                  <SelectItem value="private_pay">Private Pay</SelectItem>
                                  <SelectItem value="snap">SNAP</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={u.post_training_status || "none"}
                                onValueChange={value => {
                                  if (value === "none") return;
                                  updatePostTrainingStatus(u.user_id, {
                                    post_training_status: value,
                                    employer_name: u.post_training_employer || "",
                                    start_date: u.post_training_start_date || "",
                                    notes: u.post_training_notes || ""
                                  });
                                }}
                              >
                                <SelectTrigger className="w-44 bg-surface-muted border-slate-200" data-testid={`status-select-${u.user_id}`}>
                                  <SelectValue placeholder="Not set" />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                  <SelectItem value="none" disabled>Not set</SelectItem>
                                  <SelectItem value="Internship">Internship</SelectItem>
                                  <SelectItem value="Apprenticeship">Apprenticeship</SelectItem>
                                  <SelectItem value="On the Job Training">On the Job Training</SelectItem>
                                  <SelectItem value="Employment">Employment</SelectItem>
                                  <SelectItem value="Seeking Employment">Seeking Employment</SelectItem>
                                  <SelectItem value="Ongoing Education">Ongoing Education</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                className={u.is_certified
                                  ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                                  : "btn-outline"
                                }
                                onClick={() => updateCertification(u.user_id, !u.is_certified)}
                                data-testid={`cert-toggle-${u.user_id}`}
                              >
                                {u.is_certified ? (
                                  <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Certified</>
                                ) : (
                                  "Certify"
                                )}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1.5">
                                {u.role === "instructor" && (
                                  <Button size="sm" variant="outline" className="btn-outline"
                                    onClick={() => setCohortAssignUser(u)} data-testid={`assign-cohort-${u.user_id}`}>
                                    Assign Cohort
                                  </Button>
                                )}
                                {u.mfa_enabled && (
                                  <Button size="sm" variant="outline"
                                    className="border-amber-300 text-amber-800 hover:bg-amber-50"
                                    onClick={async () => {
                                      if (!window.confirm(`Reset MFA for ${u.email}? They will need to enroll again on next login.`)) return;
                                      try {
                                        const res = await fetch(`${API}/admin/user/${u.user_id}/mfa/reset`, {
                                          method: "POST", credentials: "include"
                                        });
                                        if (!res.ok) {
                                          const err = await res.json();
                                          throw new Error(err?.detail || "Reset failed");
                                        }
                                        toast.success(`MFA reset for ${u.email}`);
                                        fetchData();
                                      } catch (err) { toast.error(err.message); }
                                    }}
                                    data-testid={`mfa-reset-${u.user_id}`}>
                                    Reset MFA
                                  </Button>
                                )}
                                {!u.has_password && (u.auth_provider === "import" || !u.auth_provider || u.auth_provider === "email") && (
                                  <Button size="sm" variant="outline"
                                    className="border-[#FFD507] text-[#0F1B3D] hover:bg-[#FFD507]/10"
                                    onClick={async () => {
                                      try {
                                        const res = await fetch(`${API}/admin/user/${u.user_id}/send-password-setup`, {
                                          method: "POST", credentials: "include"
                                        });
                                        const text = await res.text();
                                        let data; try { data = JSON.parse(text); } catch { data = {}; }
                                        if (!res.ok) throw new Error(data?.detail || "Send failed");
                                        if (data.dev_url) {
                                          window.prompt(`DEV/PREVIEW MODE — copy this link to share with ${u.email}:`, data.dev_url);
                                        }
                                        toast.success(`Setup link sent to ${u.email}`);
                                      } catch (err) { toast.error(err.message); }
                                    }}
                                    data-testid={`send-setup-${u.user_id}`}>
                                    Send Setup Link
                                  </Button>
                                )}
                                {user?.user_id !== u.user_id && (
                                  <>
                                    {u.is_active === false ? (
                                      <Button size="sm" variant="outline"
                                        className="border-green-300 text-green-700 hover:bg-green-50"
                                        onClick={() => setUserActive(u, true)}
                                        data-testid={`activate-${u.user_id}`}>
                                        Activate
                                      </Button>
                                    ) : (
                                      <Button size="sm" variant="outline"
                                        className="border-amber-300 text-amber-800 hover:bg-amber-50"
                                        onClick={() => setUserActive(u, false)}
                                        data-testid={`deactivate-${u.user_id}`}>
                                        <Ban className="w-3.5 h-3.5 mr-1" /> Deactivate
                                      </Button>
                                    )}
                                    <Button size="sm" variant="outline"
                                      className="border-red-300 text-red-700 hover:bg-red-50"
                                      onClick={() => deleteUserPermanent(u)}
                                      data-testid={`delete-user-${u.user_id}`}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Materials (formerly Courses) Tab */}
          <TabsContent value="courses" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-slate-500">
                Materials are the learning content (videos, PDFs, modules) attached to a Program.
                Use the Programs tab to manage the career tracks themselves.
              </p>
              <Link to="/admin/courses/new">
                <Button className="btn-primary" data-testid="create-course-btn"><Plus className="w-4 h-4 mr-2" /> Add Material</Button>
              </Link>
            </div>
            <Card className="card-surface">
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-200">
                        <TableHead className="text-slate-500">Title</TableHead>
                        <TableHead className="text-slate-500">Program</TableHead>
                        <TableHead className="text-slate-500">Level</TableHead>
                        <TableHead className="text-slate-500">Duration</TableHead>
                        <TableHead className="text-slate-500">Enrollment</TableHead>
                        <TableHead className="text-slate-500">Status</TableHead>
                        <TableHead className="text-slate-500">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {courses.map(course => {
                        const program = programs.find(p => p.program_id === course.program_id);
                        return (
                        <TableRow key={course.course_id} className="border-slate-200">
                          <TableCell className="text-slate-900 font-medium">{course.title}</TableCell>
                          <TableCell>
                            {program ? (
                              <Badge className="badge-navy">{program.name}</Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-500 border-slate-200">Cross-program</Badge>
                            )}
                          </TableCell>
                          <TableCell><Badge className="badge-gold capitalize">{course.skill_level}</Badge></TableCell>
                          <TableCell className="text-slate-500">{course.duration_weeks} weeks</TableCell>
                          <TableCell className="text-slate-500">{course.current_enrollment}/{course.max_enrollment}</TableCell>
                          <TableCell>
                            <Badge className={course.is_active ? "badge-success" : "badge-error"}>
                              {course.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Link to={`/admin/courses/${course.course_id}/edit`}>
                                <Button size="sm" variant="outline" className="btn-outline h-8" data-testid={`edit-course-${course.course_id}`}>
                                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                                </Button>
                              </Link>
                              <Link to={`/courses/${course.course_id}`}>
                                <Button size="sm" variant="outline" className="btn-outline h-8" data-testid={`preview-course-${course.course_id}`}>
                                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                                </Button>
                              </Link>
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cohorts Tab */}
          <TabsContent value="cohorts" className="space-y-4">
            <CohortsTab />
          </TabsContent>



          {/* Welcome Pack Tab */}
          <TabsContent value="welcome-pack" className="space-y-4">
            <WelcomePackEditor />
          </TabsContent>

          {/* Contact Page Tab */}
          <TabsContent value="contact-page" className="space-y-4">
            <ContactPageEditor />
          </TabsContent>

          {/* Stories Tab */}
          <TabsContent value="stories" className="space-y-4">
            <StoriesAdmin />
          </TabsContent>

          {/* Site Content Tab */}
          <TabsContent value="site-content" className="space-y-6">
            {siteContent ? (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-slate-900">Landing Page Content</h3>
                    <p className="text-sm text-slate-500">Edit text displayed on the public home page.</p>
                  </div>
                  <Button onClick={saveSiteContent} disabled={!siteContentDirty || savingSiteContent}
                    className="btn-primary" data-testid="save-site-content">
                    {savingSiteContent ? "Saving..." : "Save Changes"}
                  </Button>
                </div>

                {/* Hero Section */}
                <Card className="card-surface">
                  <CardHeader><CardTitle className="text-slate-900 text-base">Hero Section</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Title (use Enter for line break)</Label>
                      <Textarea value={siteContent.hero?.title || ""} onChange={e => updateContent("hero.title", e.target.value)}
                        className="input-base mt-1" rows={2} data-testid="edit-hero-title" />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Subtitle</Label>
                      <Textarea value={siteContent.hero?.subtitle || ""} onChange={e => updateContent("hero.subtitle", e.target.value)}
                        className="input-base mt-1" rows={3} data-testid="edit-hero-subtitle" />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-slate-600 text-sm">CTA Button (signed out)</Label>
                        <Input value={siteContent.hero?.cta_text || ""} onChange={e => updateContent("hero.cta_text", e.target.value)}
                          className="input-base mt-1" data-testid="edit-hero-cta" />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">CTA Button (signed in)</Label>
                        <Input value={siteContent.hero?.cta_text_auth || ""} onChange={e => updateContent("hero.cta_text_auth", e.target.value)}
                          className="input-base mt-1" data-testid="edit-hero-cta-auth" />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">Secondary Button</Label>
                        <Input value={siteContent.hero?.secondary_btn_text || ""} onChange={e => updateContent("hero.secondary_btn_text", e.target.value)}
                          className="input-base mt-1" data-testid="edit-hero-secondary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Stats */}
                <Card className="card-surface">
                  <CardHeader><CardTitle className="text-slate-900 text-base">Stats Row</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {(siteContent.stats || []).map((stat, i) => (
                        <div key={i} className="space-y-2 p-3 border border-slate-200 rounded-lg">
                          <div>
                            <Label className="text-slate-600 text-xs">Value</Label>
                            <Input value={stat.value} onChange={e => updateContent(`stats.${i}.value`, e.target.value)}
                              className="input-base mt-1" data-testid={`edit-stat-value-${i}`} />
                          </div>
                          <div>
                            <Label className="text-slate-600 text-xs">Label</Label>
                            <Input value={stat.label} onChange={e => updateContent(`stats.${i}.label`, e.target.value)}
                              className="input-base mt-1" data-testid={`edit-stat-label-${i}`} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Features */}
                <Card className="card-surface">
                  <CardHeader><CardTitle className="text-slate-900 text-base">Features Section</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-600 text-sm">Heading</Label>
                        <Input value={siteContent.features?.heading || ""} onChange={e => updateContent("features.heading", e.target.value)}
                          className="input-base mt-1" data-testid="edit-features-heading" />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">Subtitle</Label>
                        <Input value={siteContent.features?.subtitle || ""} onChange={e => updateContent("features.subtitle", e.target.value)}
                          className="input-base mt-1" data-testid="edit-features-subtitle" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(siteContent.features?.items || []).map((item, i) => (
                        <div key={i} className="p-3 border border-slate-200 rounded-lg space-y-2">
                          <div>
                            <Label className="text-slate-600 text-xs">Feature {i + 1} Title</Label>
                            <Input value={item.title} onChange={e => updateContent(`features.items.${i}.title`, e.target.value)}
                              className="input-base mt-1" data-testid={`edit-feature-title-${i}`} />
                          </div>
                          <div>
                            <Label className="text-slate-600 text-xs">Description</Label>
                            <Textarea value={item.description} onChange={e => updateContent(`features.items.${i}.description`, e.target.value)}
                              className="input-base mt-1" rows={2} data-testid={`edit-feature-desc-${i}`} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Why Section */}
                <Card className="card-surface">
                  <CardHeader><CardTitle className="text-slate-900 text-base">Why Aspiritech Section</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Heading</Label>
                      <Input value={siteContent.why_section?.heading || ""} onChange={e => updateContent("why_section.heading", e.target.value)}
                        className="input-base mt-1" data-testid="edit-why-heading" />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {(siteContent.why_section?.items || []).map((item, i) => (
                        <div key={i}>
                          <Label className="text-slate-600 text-xs">Item {i + 1}</Label>
                          <Input value={typeof item === "string" ? item : item.text} onChange={e => updateContent(`why_section.items.${i}`, e.target.value)}
                            className="input-base mt-1" data-testid={`edit-why-item-${i}`} />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* CTA Section */}
                <Card className="card-surface">
                  <CardHeader><CardTitle className="text-slate-900 text-base">Call-to-Action Section</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-600 text-sm">Heading</Label>
                        <Input value={siteContent.cta?.heading || ""} onChange={e => updateContent("cta.heading", e.target.value)}
                          className="input-base mt-1" data-testid="edit-cta-heading" />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">Subtitle</Label>
                        <Textarea value={siteContent.cta?.subtitle || ""} onChange={e => updateContent("cta.subtitle", e.target.value)}
                          className="input-base mt-1" rows={2} data-testid="edit-cta-subtitle" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-600 text-sm">Button Text (signed out)</Label>
                        <Input value={siteContent.cta?.btn_text || ""} onChange={e => updateContent("cta.btn_text", e.target.value)}
                          className="input-base mt-1" data-testid="edit-cta-btn" />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">Button Text (signed in)</Label>
                        <Input value={siteContent.cta?.btn_text_auth || ""} onChange={e => updateContent("cta.btn_text_auth", e.target.value)}
                          className="input-base mt-1" data-testid="edit-cta-btn-auth" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Footer */}
                <Card className="card-surface">
                  <CardHeader><CardTitle className="text-slate-900 text-base">Footer</CardTitle></CardHeader>
                  <CardContent>
                    <div>
                      <Label className="text-slate-600 text-sm">Tagline</Label>
                      <Input value={siteContent.footer?.tagline || ""} onChange={e => updateContent("footer.tagline", e.target.value)}
                        className="input-base mt-1" data-testid="edit-footer-tagline" />
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-12 text-slate-500">Loading site content...</div>
            )}
          </TabsContent>

          {/* Bulk Import Tab */}
          <TabsContent value="bulk-import">
            <BulkImportTab />
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist">
            <WaitlistTab />
          </TabsContent>

          {/* State-Level Demand / Waitlist Tab */}
          <TabsContent value="state-waitlist">
            <StateWaitlistTab />
          </TabsContent>

          <TabsContent value="reports">
            <ReportsTab />
          </TabsContent>

          <TabsContent value="esignatures">
            <ESignaturesTab />
          </TabsContent>

          <TabsContent value="faqs">
            <FAQAdminTab />
          </TabsContent>

          <TabsContent value="mentors">
            <MentorApplicationsTab />
          </TabsContent>

          <TabsContent value="working-style">
            <WorkingStyleAdminTab />
          </TabsContent>

          <TabsContent value="security">
            <SecurityTab />
          </TabsContent>

          <TabsContent value="maintenance">
            <MaintenanceTab />
          </TabsContent>

          <TabsContent value="instructor-library">
            <InstructorLibraryPanel />
          </TabsContent>
        </Tabs>
        </div>

        {/* Enrollment Detail Dialog */}
        <EnrollmentDetailDialog
          enrollment={selectedEnrollment}
          open={detailOpen}
          onClose={setDetailOpen}
          onStatusChange={updateEnrollmentStatus}
        />

        {/* Cohort Assignment Dialog */}
        {/* Add User modal */}
        <Dialog open={addUserOpen} onOpenChange={(o) => { if (!o) { setAddUserOpen(false); setNewUser({ name: "", email: "", role: "participant" }); } }}>
          <DialogContent className="bg-white border-slate-200" data-testid="add-user-dialog">
            <DialogHeader>
              <DialogTitle className="text-slate-900">Add a new user</DialogTitle>
              <DialogDescription className="text-slate-500">
                We’ll email them a secure link to set their own password. No password is stored here.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label className="text-slate-700">Full Name *</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser((p) => ({ ...p, name: e.target.value }))}
                  className="input-base" placeholder="Jane Doe" data-testid="new-user-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-700">Email *</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
                  className="input-base" placeholder="jane@example.com" data-testid="new-user-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-700">Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser((p) => ({ ...p, role: v }))}>
                  <SelectTrigger className="input-base" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="participant">Participant</SelectItem>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="mentor">Mentor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">Admins and instructors will be required to set up MFA on first login.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" className="btn-outline" onClick={() => setAddUserOpen(false)} disabled={creatingUser} data-testid="add-user-cancel">Cancel</Button>
                <Button className="btn-primary gap-2" onClick={createUser} disabled={creatingUser} data-testid="add-user-submit">
                  {creatingUser ? "Adding…" : (<><UserPlus className="w-4 h-4" /> Add &amp; Send Invite</>)}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>


        <Dialog open={!!cohortAssignUser} onOpenChange={() => { setCohortAssignUser(null); setAssignProgramId(""); setAssignCohortId(""); }}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-slate-900">
                Assign Cohort to {cohortAssignUser?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label className="text-slate-600 text-sm">Program</Label>
                <Select value={assignProgramId} onValueChange={(v) => { setAssignProgramId(v); setAssignCohortId(""); }}>
                  <SelectTrigger className="input-base mt-1" data-testid="assign-program-select"><SelectValue placeholder="Select program..." /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {programs.filter(p => p.cohorts?.length > 0).map(p => (
                      <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {assignProgramId && (
                <div>
                  <Label className="text-slate-600 text-sm">Cohort</Label>
                  <Select value={assignCohortId} onValueChange={setAssignCohortId}>
                    <SelectTrigger className="input-base mt-1" data-testid="assign-cohort-select"><SelectValue placeholder="Select cohort..." /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {(programs.find(p => p.program_id === assignProgramId)?.cohorts || []).map(c => (
                        <SelectItem key={c.cohort_id} value={c.cohort_id}>
                          {new Date(c.start_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} - {c.instructors?.join(", ") || "TBD"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button onClick={assignCohort} disabled={!assignProgramId || !assignCohortId}
                className="btn-primary w-full" data-testid="confirm-assign-cohort">
                Assign to Cohort
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PageLayout>
  );
};

export default AdminPanel;
